/**
 * The one place a screen can refuse a navigation away from itself.
 *
 * The Budget screen holds unsaved edits, and the five-tab bar is drawn *outside*
 * the routes (`tab_bar.tsx`) — so the control that would throw a draft away is
 * not in the subtree that owns it. There is no router-level answer available
 * either: `useBlocker` needs a data router, and this app mounts plain `<Routes>`
 * inside a `BrowserRouter`.
 *
 * So the guard is a module-level registry the tab bar consults on every tap. Two
 * deliberate limits, both of which keep it from becoming ambient global state:
 *
 * - **One guard at a time.** Exactly one screen in this app has a draft. A
 *   second registration replacing the first would silently disarm it, so
 *   registering over a live guard throws rather than winning (INV-7).
 * - **Unregistering is scoped.** `register` returns its own teardown, and that
 *   teardown clears only the guard it installed — so a late unmount cannot
 *   disarm a guard some other screen has since installed.
 *
 * A guard returns `true` to mean *"I have taken responsibility for this"* — the
 * caller then suppresses its own navigation and the guard shows whatever it
 * needs to. Returning `false` means "not mine", and navigation proceeds.
 */

export type NavGuard = (to: string) => boolean;

let current: NavGuard | null = null;

/** Install the guard. Throws if one is already live — see the note above. */
export function registerNavGuard(guard: NavGuard): () => void {
  if (current !== null) {
    throw new Error("A navigation guard is already registered. Only one screen may hold one.");
  }
  current = guard;

  return () => {
    if (current === guard) current = null;
  };
}

/**
 * Ask the live guard, if any, whether it is taking this navigation.
 *
 * `false` when nothing is registered, so every call site is a plain
 * "navigate unless told otherwise".
 */
export function interceptNavigation(to: string): boolean {
  return current === null ? false : current(to);
}

/** Marks the extra history entry this module pushes, so it is recognisable. */
const SENTINEL = "aegaeUnsavedGuard";

/**
 * Also catch the **back gesture**, which `registerNavGuard` structurally cannot.
 *
 * The tab bar and the header Back both route through this app's own code, so a
 * guard can be asked first. An Android back press, an iOS edge-swipe and the
 * browser's Back button do not: they are a `popstate`, react-router unmounts the
 * screen, and the draft is gone. `beforeunload` does not fire either — that is
 * for leaving the *document*, and this never leaves it. On an installed mobile
 * PWA the gesture is the primary way out of a sub-screen, so without this the
 * warning is missing exactly where it is most needed.
 *
 * The mechanism is one **sentinel history entry**, pushed at the same URL. The
 * next Back consumes it instead of leaving the route; the handler immediately
 * pushes a fresh one (so the guard survives repeated presses) and reports.
 *
 * `release` is idempotent and takes an explicit choice about that entry:
 *
 * - `release()` — the screen is **staying** (saved, or discarded in place), so
 *   the sentinel is wound back off the stack. Without this, Back would do
 *   nothing once before working, which reads as a broken button.
 * - `release(false)` — the caller is **navigating away itself** and will replace
 *   the sentinel with the destination. Unwinding here instead would fire a
 *   `history.back()` that lands after the caller's own navigation and drag the
 *   user back off the screen they just chose.
 */
export function armBackGuard(onBack: () => void): (unwind?: boolean) => void {
  window.history.pushState({ [SENTINEL]: true }, "");
  let live = true;

  const onPop = (): void => {
    if (!live) return;
    // The browser has already consumed the sentinel getting here. Re-arm before
    // reporting, so a second press is caught too.
    window.history.pushState({ [SENTINEL]: true }, "");
    onBack();
  };
  window.addEventListener("popstate", onPop);

  return (unwind = true) => {
    if (!live) return;
    live = false;
    window.removeEventListener("popstate", onPop);
    // Only ever pop an entry this module pushed. Anything else on top means a
    // navigation happened underneath us and going back would be somebody's real
    // history rather than our bookkeeping.
    const state: unknown = window.history.state;
    const isOurs =
      typeof state === "object" && state !== null && SENTINEL in (state as Record<string, unknown>);
    if (unwind && isOurs) window.history.back();
  };
}
